// Type definitions for the correct data hierarchy model
// Implementing dual filtering logic as per HIERARCHY-ANALYSIS.md

export interface Type {
  id: string;
  name: string;
  description?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Unit {
  id: string;
  name: string;
  typeId: string;           // Units belong to a Type
  description?: string;
  isActive?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface CostCenter {
  id: string;
  name: string;
  typeId: string;           // Cost Centers are defined at Type level (not Unit level)
  description?: string;
  displayName?: string;     // Optional: For UI clarity like "Aravu (Mill)"
  isActive?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Category {
  id: string;
  name: string;
  typeId: string;           // Categories belong to a Type
  costCenterId: string;     // Categories are under a Cost Center
  description?: string;
  needsNameField?: boolean;
  supportsUnits?: boolean;
  unitLabel?: string;
  isActive?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Expense {
  id: string;
  typeId: string;           // Type reference
  unitId: string;           // Unit reference
  costCenterId: string;     // Cost Center reference
  categoryId: string;       // Category reference
  amount: number;
  date: Date;
  description?: string;
  // ...other expense fields
}

// Filtering interfaces for dual logic
export interface MasterDataFilters {
  typeId: string;
  // These are filtered independently based on Type
  availableUnits: Unit[];
  availableCostCenters: CostCenter[];
  availableCategories: Category[];
}

export interface ExpenseDataFilters {
  typeId: string;           // Base filter
  unitIds: string[];        // Selected Units
  costCenterIds: string[];  // Selected Cost Centers (independent from Units)
  categoryIds?: string[];   // Optional: Selected Categories
}

// For expense reporting - intersection of Unit+CostCenter
export interface ExpenseQueryParams {
  typeId: string;
  unitId: string;
  costCenterId: string;
  categoryId?: string;
  dateFrom?: Date;
  dateTo?: Date;
}

// Validation rules
export interface ValidationRule {
  field: string;
  rule: string;
  message: string;
}

export const HIERARCHY_VALIDATION_RULES: ValidationRule[] = [
  {
    field: 'type-unit',
    rule: 'Units must belong to the selected Type',
    message: 'Selected units must be of the same type for meaningful comparison'
  },
  {
    field: 'type-costcenter',
    rule: 'Cost Centers must belong to the selected Type',
    message: 'Cost Centers are type-specific and cannot be mixed across types'
  },
  {
    field: 'cross-type-comparison',
    rule: 'Prevent cross-type comparisons',
    message: 'Comparing units or cost centers across different types is not meaningful'
  }
];
